/**
 * TDM Drawer — accordion navigation + image crossfade
 *
 * - Toggles L1 accordion sections on click
 * - Crossfades the image panel to match hovered/active L1 item
 * - Resets state when the drawer closes
 */

const SELECTORS = {
  root: '[data-tdm-drawer]',
  toggle: '[data-tdm-toggle]',
  item: '.tdm-nav__item',
  imgPanel: '[data-tdm-img-panel]',
  l2Toggle: '[data-tdm-l2-toggle]',
  l2Item: '.tdm-nav__l2-item--has-children',
};

class TDMDrawer {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.menuDrawer = root.closest('.menu-drawer');
    this.details = root.closest('details');
    this.items = Array.from(root.querySelectorAll(SELECTORS.item));
    this.toggles = Array.from(root.querySelectorAll(SELECTORS.toggle));
    this.imgPanels = Array.from(root.querySelectorAll(SELECTORS.imgPanel));

    this.#bind();
  }

  #bind() {
    // Accordion toggle on click
    this.toggles.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const item = btn.closest(SELECTORS.item);
        if (!item) return;

        const wasExpanded = item.classList.contains('is-expanded');

        // Close all other expanded items
        this.items.forEach((el) => {
          if (el !== item && el.classList.contains('is-expanded')) {
            el.classList.remove('is-expanded');
            const toggle = el.querySelector(SELECTORS.toggle);
            if (toggle) toggle.setAttribute('aria-expanded', 'false');
          }
        });

        // Toggle this item
        item.classList.toggle('is-expanded', !wasExpanded);
        btn.setAttribute('aria-expanded', !wasExpanded ? 'true' : 'false');

        // Update image
        this.#showImage(item.dataset.tdmImg);
      });
    });

    // L2 accordion toggle (L3 sub-items)
    this.root.querySelectorAll(SELECTORS.l2Toggle).forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const l2Item = btn.closest(SELECTORS.l2Item);
        if (!l2Item) return;

        const wasExpanded = l2Item.classList.contains('is-l2-expanded');

        // Collapse siblings within same L2 list
        const siblingList = l2Item.parentElement;
        if (siblingList) {
          siblingList.querySelectorAll(SELECTORS.l2Item).forEach((el) => {
            if (el !== l2Item && el.classList.contains('is-l2-expanded')) {
              el.classList.remove('is-l2-expanded');
              const sibling = el.querySelector(SELECTORS.l2Toggle);
              if (sibling) sibling.setAttribute('aria-expanded', 'false');
            }
          });
        }

        l2Item.classList.toggle('is-l2-expanded', !wasExpanded);
        btn.setAttribute('aria-expanded', !wasExpanded ? 'true' : 'false');
      });
    });

    // Image switch on hover (all items, not just toggles)
    this.items.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        this.#showImage(item.dataset.tdmImg);
      });
    });

    // Reset when drawer closes
    if (this.details) {
      const observer = new MutationObserver(() => {
        if (!this.details.hasAttribute('open')) {
          this.#reset();
        }
      });
      observer.observe(this.details, { attributes: true, attributeFilter: ['open'] });
    }
  }

  /**
   * Show the image panel matching the given key; fall back to "default".
   * @param {string} [key]
   */
  #showImage(key) {
    if (!this.imgPanels.length) return;

    const target = this.imgPanels.find(
      (p) => p.dataset.tdmImgPanel === key
    );

    this.imgPanels.forEach((p) => {
      p.classList.toggle('is-active', p === (target || this.imgPanels[0]));
    });
  }

  /** Collapse all accordions and reset image to default */
  #reset() {
    this.items.forEach((el) => {
      el.classList.remove('is-expanded');
      const toggle = el.querySelector(SELECTORS.toggle);
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
    // Also collapse any open L2 items
    this.root.querySelectorAll(SELECTORS.l2Item).forEach((el) => {
      el.classList.remove('is-l2-expanded');
      const btn = el.querySelector(SELECTORS.l2Toggle);
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
    this.#showImage('default');
  }
}

// Boot
function boot() {
  document.querySelectorAll(SELECTORS.root).forEach((root) => {
    if (!(root instanceof HTMLElement)) return;
    if (root.dataset.tdmDrawerInit === 'true') return;
    root.dataset.tdmDrawerInit = 'true';
    new TDMDrawer(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
